import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  ChartBarIcon,
  EyeIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import logger from '../../utils/logger';

const TestView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const testId = params.id;
  const backToLibraryPath = '/dashboard/library/assessments';

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestions, setShowQuestions] = useState(true);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`/api/tests/library/${testId}`);

        if (response.data.test) {
          setTest(response.data.test);
        } else {
          toast.error('No test data received');
        }
      } catch (error) {
        if (error.response?.status === 404) {
          toast.error('Test not found');
        } else if (error.response?.status === 401) {
          toast.error('Authentication required');
        } else {
          toast.error('Failed to load test details');
        }

        // Don't navigate away immediately, let user see the error
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    if (testId) {
      // Basic validation for MongoDB ObjectId format
      if (testId.length !== 24) {
        toast.error('Invalid test ID format');
        setLoading(false);
        return;
      }

      fetchTest();
    } else {
      setLoading(false);
    }
  }, [testId, navigate]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    const roleIcons = {
      Accountant: '💰',
      HR: '👥',
      Taxation: '📊',
      Legal: '⚖️',
      Marketing: '📢',
      Sales: '💼',
      Administration: '🏢',
      Finance: '💳',
      MR: '💊',
      IT: '💻',
      Engineering: '🔧',
      Healthcare: '🏥',
      Education: '📚',
      Retail: '🛍️',
      Manufacturing: '🏭',
    };
    return roleIcons[role] || '📋';
  };

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minutes`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug: Show current state
  logger.log('🔍 Current component state:', { loading, test, testId });

  if (!test) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Test not found</h2>
            <p className="text-gray-600 mb-4">
              The test you're looking for doesn't exist or has been removed.
              <br />
              <strong>Test ID:</strong> {testId}
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-sm text-gray-500">Debugging info:</p>
              <p className="text-sm text-gray-500">Loading: {loading.toString()}</p>
              <p className="text-sm text-gray-500">Test ID: {testId}</p>
            </div>
            <button
              onClick={() => navigate(backToLibraryPath)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Test Library
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Debug Info - Remove this later */}
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 max-w-4xl mx-auto">
        <strong>Debug Info:</strong> testId: {testId}, loading: {loading.toString()}, test:{' '}
        {test ? 'Loaded' : 'Not loaded'}
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(backToLibraryPath)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Test Library
        </button>

        {/* Test Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-4xl">{getRoleIcon(test.role)}</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{test.title}</h1>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getDifficultyColor(test.difficulty)}`}
                >
                  {test.difficulty}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Used</div>
              <div className="text-2xl font-bold text-gray-900">{test.usageCount || 0}</div>
              <div className="text-xs text-gray-500">times</div>
            </div>
          </div>

          {test.description && <p className="text-gray-600 mb-4">{test.description}</p>}

          {/* Test Metadata Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center space-x-2 text-gray-600">
              <UserIcon className="h-5 w-5" />
              <span className="text-sm">{test.role}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <ClockIcon className="h-5 w-5" />
              <span className="text-sm">{formatTime(test.estimatedTime)}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <QuestionMarkCircleIcon className="h-5 w-5" />
              <span className="text-sm">{test.numberOfQuestions} questions</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <ChartBarIcon className="h-5 w-5" />
              <span className="text-sm">{test.totalPoints} points</span>
            </div>
          </div>

          {/* Tags */}
          {test.tags && test.tags.length > 0 && (
            <div className="flex items-center space-x-2 mb-4">
              <TagIcon className="h-4 w-4 text-gray-400" />
              <div className="flex flex-wrap gap-2">
                {test.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center">
            <button
              onClick={() => setShowQuestions(!showQuestions)}
              className="flex items-center px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              {showQuestions ? 'Hide Questions' : 'Show Questions'}
            </button>
          </div>
        </div>

        {/* Questions Section */}
        {showQuestions && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Questions</h2>
            <div className="space-y-6">
              {test.questions.map((questionData, index) => {
                const question = questionData.question;

                // Debug logging
                logger.log(`🔍 Question ${index + 1}:`, {
                  title: question.title,
                  type: question.type,
                  hasOptions: !!question.options,
                  optionsLength: question.options?.length,
                  hasCorrectAnswer: !!question.correctAnswer,
                  correctAnswer: question.correctAnswer,
                });

                return (
                  <div key={question._id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                          {index + 1}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(question.difficulty)}`}
                        >
                          {question.difficulty}
                        </span>
                        <span className="text-xs text-gray-500">{question.estimatedTime}s</span>
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {questionData.points} point{questionData.points !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-2">{question.title}</h3>

                    {question.content && question.content !== question.title && (
                      <p className="text-gray-600 text-sm mb-3">{question.content}</p>
                    )}

                    {/* Question Type and Options */}
                    <div className="text-xs text-gray-500 mb-2">Type: {question.type}</div>

                    {/* Display options for MCQ questions */}
                    {question.options && question.options.length > 0 && (
                      <div className="space-y-2">
                        {question.options.map((option, optionIndex) => (
                          <div key={optionIndex} className="flex items-center space-x-2 text-sm">
                            <span className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                              {question.type.includes('Multiple') ? (
                                <div className="w-2 h-2 bg-gray-300 rounded-sm"></div>
                              ) : (
                                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                              )}
                            </span>
                            <span
                              className={
                                option.isCorrect ? 'text-green-600 font-medium' : 'text-gray-700'
                              }
                            >
                              {option.text}
                            </span>
                            {option.isCorrect && <span className="text-green-600 text-xs">✓</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Display correct answer for non-MCQ questions */}
                    {question.options.length === 0 && question.correctAnswer && (
                      <div className="mt-3 p-3 bg-green-50 rounded-md border border-green-200">
                        <div className="text-xs font-medium text-green-800 mb-1">
                          Correct Answer:
                        </div>
                        <div className="text-sm text-green-700">
                          {question.type === 'True/False'
                            ? // For True/False, map 'a' to 'True' and 'b' to 'False'
                              question.correctAnswer === 'a'
                              ? 'True'
                              : question.correctAnswer === 'b'
                                ? 'False'
                                : question.correctAnswer
                            : // For other types, display the correct answer directly
                              question.correctAnswer}
                        </div>
                      </div>
                    )}

                    {question.explanation && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-md">
                        <div className="text-xs font-medium text-blue-800 mb-1">Explanation:</div>
                        <div className="text-sm text-blue-700">{question.explanation}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Test Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Category:</span>
              <span className="ml-2 text-gray-600">{test.category}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Passing Score:</span>
              <span className="ml-2 text-gray-600">{test.passingScore}%</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Created:</span>
              <span className="ml-2 text-gray-600">
                {new Date(test.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Last Updated:</span>
              <span className="ml-2 text-gray-600">
                {new Date(test.lastUpdated).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Version:</span>
              <span className="ml-2 text-gray-600">{test.version}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Average Score:</span>
              <span className="ml-2 text-gray-600">
                {test.averageScore > 0 ? `${test.averageScore.toFixed(1)}%` : 'No data'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestView;
